import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, Text } from '../../../../components';
import { GenerateButton } from '../../../creatives/components/GenerateButton';
import { ART_STYLE } from '../../../../theme/tokens';
import type { ArtStyleStatus } from '../../art-style-api';
import type { SaveState } from '../../useArtStyle';
import {
  Root,
  NameBlock,
  Eyebrow,
  NameRow,
  NameInput,
  NameEdit,
  Meta,
  SaveLine,
  SaveSpinner,
  RetryLink,
} from './ArtStyleHeader.styles';

export type ArtStyleHeaderProps = {
  name: string;
  status: ArtStyleStatus;
  /** The website shows this style (its Primary badge): then "Regenerate website art" replaces "Set as primary".
   *  Defaults to the style being the active one (a service without catalog looks). */
  primary?: boolean;
  saveState: SaveState;
  /** The style has a definition, so it can be made primary. */
  canActivate: boolean;
  /** An action is running (the buttons wait for it). */
  locked: boolean;
  onRename: (name: string) => void;
  onRetrySave: () => void;
  /** Makes this the brand's primary style (the website's look, which its banners and images follow too). */
  onSetPrimary: () => void;
  /** Credits a website art regeneration costs, shown on "Regenerate website art" (undefined until the price table
   *  loads: no price rather than a guess). */
  regenerateCost?: number;
  /** Opens the "Regenerate website art" sheet (shown once this style is primary). */
  onRegenerate: () => void;
  /** A website art job runs for the brand (the regenerate action waits for it to end). */
  siteBusy?: boolean;
  /** "Set as primary" waits too (it changes the website's look). */
  primaryLocked?: boolean;
};

/** Longest style name the field accepts (a slug-like label, not prose). */
const MAX_NAME = 60;

/**
 * The style's name (editable in place; Lumio suggests one on the first extraction), the autosave indicator and the
 * style's one action: "Set as primary" (shown once a style is extracted) makes it the style the brand's banners
 * and images follow; once it is primary, "Regenerate website art" takes its place. "Saved" lingers briefly after a
 * save, then settles away.
 */
export const ArtStyleHeader = ({
  name,
  status,
  primary,
  saveState,
  canActivate,
  locked,
  onRename,
  onRetrySave,
  onSetPrimary,
  onRegenerate,
  regenerateCost,
  siteBusy = false,
  primaryLocked = false,
}: ArtStyleHeaderProps): React.ReactElement => {
  const { t } = useTranslation();
  const nameRef = React.useRef<HTMLInputElement>(null);
  const active = primary ?? status === 'active';
  const canSetPrimary = canActivate && !locked;
  // The "Saved" confirmation holds for a moment after each save, then fades out.
  const [savedVisible, setSavedVisible] = React.useState(false);
  React.useEffect(() => {
    if (saveState !== 'saved') {
      setSavedVisible(false);
      return undefined;
    }
    setSavedVisible(true);
    const timer = window.setTimeout(() => setSavedVisible(false), ART_STYLE.SAVED_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [saveState]);

  return (
    <Root>
      <NameBlock>
        <Eyebrow text="Your art style" tag="span" />
        <NameRow>
          <NameInput
            ref={nameRef}
            value={name}
            maxLength={MAX_NAME}
            // Sized to its text (field-sizing in the styles; this is the fallback) so the pencil hugs the name.
            size={Math.max((name || t('Name your style')).length + 1, 4)}
            placeholder={t('Name your style')}
            aria-label={t('Style name')}
            onChange={(event) => onRename(event.target.value)}
          />
          {/* The pencil says the name is editable; it focuses the field. */}
          <NameEdit type="button" aria-label={t('Edit name')} onClick={() => nameRef.current?.focus()}>
            <Icon name="edit" size={16} />
          </NameEdit>
        </NameRow>
      </NameBlock>
      <Meta>
        <SaveLine
          role="status"
          aria-live="polite"
          $visible={saveState === 'saving' || saveState === 'error' || savedVisible}
        >
          {saveState === 'saving' ? (
            <>
              <SaveSpinner aria-hidden="true" />
              <Text text="Saving…" tag="span" />
            </>
          ) : saveState === 'error' ? (
            <>
              <Icon name="warning" size={14} />
              <Text text="Not saved" tag="span" />
              <RetryLink type="button" onClick={onRetrySave}>
                <Text text="Retry" tag="span" />
              </RetryLink>
            </>
          ) : (
            <>
              <Icon name="check" size={14} />
              <Text text="Saved" tag="span" />
            </>
          )}
        </SaveLine>
        {active ? (
          <GenerateButton
            compact
            label={t('Regenerate website art')}
            cost={regenerateCost}
            disabled={locked || siteBusy}
            onClick={() => {
              if (!locked && !siteBusy) onRegenerate();
            }}
          />
        ) : canActivate ? (
          <Button
            label={t('Set as primary')}
            disabled={!canSetPrimary || primaryLocked}
            onClick={() => {
              if (canSetPrimary && !primaryLocked) onSetPrimary();
            }}
          />
        ) : null}
      </Meta>
    </Root>
  );
};

export default ArtStyleHeader;
